/**
 * The table. One component, two sizes.
 *
 * This replaced a screen that only existed on a television. Everybody now sees
 * the table on whatever they are holding: `full` for a shared screen, and
 * `backdrop` for a phone, where it sits behind the player's own hand. The same
 * markup and the same data either way — the variant changes how big things are
 * and how much chrome is around them, never what is true.
 *
 * **Identical for every viewer in what it shows**, with one exception that is
 * the point of the change: it is *rotated* so the viewer is at the bottom. What
 * is drawn comes from the table section alone and never from anybody's personal
 * overlay, so rotating it cannot leak anything — it only moves it.
 *
 * It holds no authority. A presence without a seat may ask for a sync and
 * nothing else, so there is nothing here to click.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useCounterEvent } from '../render/use-counter-event.js';
import { FLIGHT_MS, landedCard } from '../render/arrival.js';
import { tallyText } from '../render/tally.js';
import { SEAT_END } from '@cards/toolkit';
import type { MatchView } from '../net/match.js';
import type { GameLayoutWire, SeatActionWire, ZoneSpecWire } from '../net/session.js';
import { Card } from '../render/Card.js';
import { Zone } from '../render/Zone.js';
import { backsShown, seatPlacement } from '../render/narrow.js';
import { useNarrow } from './use-narrow.js';
import { badgeCount, resolve } from '../render/layout.js';
import { avatarEmoji } from '../render/avatars.js';
import { percent, placeSeats } from '../render/seating.js';
import { seatsOffered } from '../render/seat-actions.js';
import { useGrace } from './use-grace.js';
import type { CountsByMove } from './use-grace.js';
import type { SeatPlacement } from '../render/seating.js';
import { SeatAvatarRing } from '../render/Clock.js';
import { sidesOf, sideName } from '../render/teams.js';
import { seatColour } from '../render/seat-colour.js';
import { Scoreboard } from './Scoreboard.js';
import { Shuffling } from './Shuffling.js';
import { ChipStack } from '../render/ChipStack.js';
import { committedOf, middleOf, potOf } from '../render/chips.js';
import { TrickWon } from './TrickWon.js';
import { Showdown } from './Showdown.js';
import { PAYOUT_MS, SHOWDOWN_MS, payoutOf, readShowdown } from '../render/showdown.js';
import { moneyMoves } from '../render/money.js';
import type { Money } from '../render/money.js';
import { useSession } from '../session-context.js';

export type TableVariant = 'full' | 'backdrop';

/** Who played what in the current trick, when the game reports it. */
interface TrickPlay {
  seat: number;
  card: string;
}

/**
 * How many times this game has recycled its discard pile.
 *
 * A game that never recycles simply never reports it, and the indicator never
 * fires — so this needs no per-game knowledge beyond the field's name.
 */
function reshuffleCount(view: MatchView): number {
  const value = view.view['reshuffles'];
  return typeof value === 'number' ? value : 0;
}

function trickPlays(view: MatchView): TrickPlay[] {
  const plays = view.view['trickPlays'];
  if (!Array.isArray(plays)) return [];
  return plays.filter(
    (play): play is TrickPlay =>
      typeof play === 'object' &&
      play !== null &&
      typeof (play as TrickPlay).seat === 'number' &&
      typeof (play as TrickPlay).card === 'string',
  );
}

/** Where a motion starts or ends, as a percentage of the ring. */
/**
 * A place on the ring, as a FRACTION of it: 0.5, 0.5 is the middle.
 *
 * The unit is the whole point of this comment. `placeSeats` produces fractions
 * and `percent()` multiplies by 100, so anything that measures the DOM has to
 * hand back a fraction too - `zonePoint` returned 0-100 for a while and every
 * flight to or from a named zone was rendered at `4700%`, forty-seven ring
 * widths off the side of the table. What that looked like was a card coming in
 * from nowhere, far too fast to read.
 */
interface Point {
  /** 0 at the ring's left edge, 1 at its right. */
  x: number;
  /** 0 at the ring's top, 1 at its bottom. */
  y: number;
}

/** A card in flight: where from, where to, and which flight this is. */
/** A card on its way to a zone, and what that zone shows until it arrives. */
interface Landing {
  readonly card: string;
  readonly beneath: string | undefined;
}

interface Flight extends Record<string, unknown> {
  key: number;
  from: Point;
  to: Point;
  /** The card's face, where the table can tell which card it is. */
  card?: string | undefined;
  /** Money rather than a card, and how much of it. See `usePayout`. */
  chips?: number | undefined;
  /** Why the money is moving. On the element as `data-chips`, for tests. */
  kind?: 'bet' | 'sweep' | 'payout' | undefined;
}

/**
 * Chips going onto the felt, and the felt going into the pot.
 *
 * Driven by the NUMBERS rather than by `lastMove`, because only one of the two
 * is a move. A bet arrives with an action; a sweep is what happens when a
 * betting round closes and nobody does it at all. Reading the money means both
 * are seen the same way - and means this knows nothing about poker, or about
 * which moves a game calls betting.
 *
 * The amounts and the difference between a sweep and a settle are worked out
 * by `moneyMoves`, which is tested; this is the part that turns them into
 * things flying across a table.
 */
function useChipMoves(
  view: MatchView,
  layout: GameLayoutWire,
  chairPoint: (seat: number) => Point | null,
  seatPoint: (seat: number) => Point | null,
  potPoint: () => Point | null,
): Flight[] {
  const [flights, setFlights] = useState<Flight[]>([]);

  const seats = view.seats.length;
  const pot = potOf(view, layout);
  const hand = view.view['handNumber'];

  /*
   * The money as this packet reports it, rebuilt each render and compared
   * against the last one inside the effect - so a packet that changed nothing
   * about the money animates nothing, which is most packets.
   */
  const now: Money | null =
    pot === null
      ? null
      : {
          committed: Array.from({ length: seats }, (_, seat) => committedOf(view, layout, seat)),
          pot,
          hand: typeof hand === 'number' ? hand : 0,
        };

  const points = useRef({ chairPoint, seatPoint, potPoint });
  points.current = { chairPoint, seatPoint, potPoint };

  /** The money as it was. Null until the first packet, which is the baseline. */
  const was = useRef<Money | null>(null);
  /**
   * While the showdown panel is explaining the last hand, money stays still.
   *
   * The next hand is dealt the instant one settles and is bet into
   * immediately, so without this the blinds and calls of hand N+1 went
   * skidding across the panel that was still explaining hand N. The panel is
   * a frozen moment and the table underneath is not - the third time that has
   * needed saying, and the same answer each time.
   *
   * Only the payout moves during the panel, because the payout is what the
   * panel is about.
   */
  const quietUntil = useRef(0);
  /** Rises per flight, so two bets of the same size never share a key. */
  const serial = useRef(0);

  useEffect(() => {
    if (!now) return;
    const before = was.current;
    was.current = now;

    /*
     * The first packet is a baseline, never an event.
     *
     * Somebody joining a table mid-hand would otherwise watch every bet
     * already on the felt fly into place, and a reconnect would replay the
     * hand so far as one burst.
     */
    if (!before) return;

    /*
     * A settled hand starts the quiet window, and settles nothing itself -
     * `moneyMoves` already returns nothing across a hand boundary.
     */
    if (now.hand !== before.hand) {
      const explained = (readShowdown(view)?.hands.length ?? 0) > 0;
      quietUntil.current = performance.now() + (explained ? SHOWDOWN_MS : 0);
      return;
    }
    if (performance.now() < quietUntil.current) return;

    const moves = moneyMoves(before, now);
    if (moves.length === 0) return;

    const at = points.current;
    const added: Flight[] = [];
    for (const move of moves) {
      const from = move.kind === 'bet' ? at.chairPoint(move.seat) : at.seatPoint(move.seat);
      const to = move.kind === 'bet' ? at.seatPoint(move.seat) : at.potPoint();
      if (!from || !to) continue;
      serial.current += 1;
      /* Keys below the payout's negative space and the moves' positive one. */
      added.push({
        key: -1_000_000 - serial.current,
        from,
        to,
        chips: move.amount,
        kind: move.kind,
      });
    }
    if (added.length === 0) return;

    setFlights((current) => [...current, ...added]);
    const keys = new Set(added.map((flight) => flight.key));
    const timer = window.setTimeout(() => {
      setFlights((current) => current.filter((flight) => !keys.has(flight.key)));
    }, FLIGHT_MS + 300);
    return () => {
      window.clearTimeout(timer);
    };
    /* Compared by value inside, so the object identity `now` changes with
       every render is not a reason to re-run. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [now?.pot, now?.hand, JSON.stringify(now?.committed)]);

  return flights;
}

/**
 * The pot going to whoever won it.
 *
 * Fires on the same counter the showdown panel does, a beat later and WHILE it
 * is still up. The panel is the frozen moment; the chips move inside it.
 *
 * Paying once the panel cleared was the first attempt and it was wrong on a
 * real table: the next hand is dealt the instant this one settles and plays on
 * underneath, so the pot of a finished hand went sliding across a board that
 * had already reached the turn. See `PAYOUT_MS`.
 *
 * Works for a folded-out hand too, which has no panel at all: the chips are
 * then the ONLY thing that says who won, and they set off immediately.
 *
 * Nothing here knows about poker. The amounts come from the game's own record
 * of what it paid, and a game that publishes none never animates.
 */
function usePayout(
  view: MatchView,
  seatPoint: (seat: number) => Point | null,
  potPoint: () => Point | null,
): Flight[] {
  const [flights, setFlights] = useState<Flight[]>([]);
  const result = readShowdown(view);

  /* Through a ref, for the reason `useCounterEvent` documents: this object is
     rebuilt every render and naming it would re-run the effect constantly. */
  const latest = useRef({ result, seatPoint, potPoint });
  latest.current = { result, seatPoint, potPoint };

  /** A payout waiting for its panel to clear, so a newer hand can cancel it. */
  const pending = useRef<number | null>(null);

  useCounterEvent(result?.n ?? 0, () => {
    const at = latest.current;
    const hand = at.result;
    if (!hand) return;

    /*
     * The newest hand wins.
     *
     * Hands can finish faster than the panel explains them - a checked-down
     * hand is eight taps - and the pot of the hand before last crossing the
     * table while THIS hand is being read is money moving for no visible
     * reason. Seen happening: a payout landed under the next hand's panel.
     */
    if (pending.current !== null) window.clearTimeout(pending.current);

    /*
     * Measured when the chips leave, not now.
     *
     * The pot is still on the table at this moment and about to be swept, and
     * the seats may yet be re-laid out. Reading the geometry inside the
     * timeout is what makes the flight start from where the pot actually is.
     */
    const send = () => {
      const from = at.potPoint() ?? { x: 0.5, y: 0.5 };
      const added: Flight[] = [];
      for (const taker of payoutOf(hand)) {
        const to = at.seatPoint(taker.seat);
        if (!to) continue;
        /* Negative keys, so a payout can never collide with a move's `n`. */
        added.push({
          key: -(hand.n * 100 + taker.seat),
          from,
          to,
          chips: taker.amount,
          kind: 'payout',
        });
      }
      if (added.length === 0) return;
      setFlights((current) => [...current, ...added]);
      window.setTimeout(() => {
        const keys = new Set(added.map((flight) => flight.key));
        setFlights((current) => current.filter((flight) => !keys.has(flight.key)));
      }, FLIGHT_MS + 300);
    };

    /* A showdown is read first, then paid - inside the panel, not after it.
       A fold-out has no panel and nothing to explain, so it pays at once. */
    const wait = hand.hands.length > 0 ? PAYOUT_MS : 0;
    pending.current = window.setTimeout(() => {
      pending.current = null;
      send();
    }, wait);
  });

  return flights;
}

/**
 * A card travelling, driven by the table's `lastMove` counter.
 *
 * The renderer knows no game: which move travels between which places is
 * declared in the layout's `motions`, exactly as `seatBadges` declares what a
 * seat shows. A game that declares none simply never animates.
 *
 * Both ends are percentages of the ring, so the same component works at both
 * sizes. A SEAT end is computed from the seating geometry; a ZONE end is
 * measured from the DOM, because only the layout engine knows where the draw
 * pile actually ended up.
 *
 * Measuring matters: these used to resolve to the middle of the table, so a
 * card drawn from a pile on the left appeared to come from the centre and the
 * animation pointed at nothing. The middle remains the fallback for a zone
 * that is not on screen.
 */
function useFlights(
  view: MatchView,
  layout: GameLayoutWire,
  variant: TableVariant,
  seatPoint: (seat: number) => Point | null,
  zonePoint: (zone: string) => Point | null,
  slotPoint: () => Point | null,
): { flights: Flight[]; landing: Record<string, Landing> } {
  const [flights, setFlights] = useState<Flight[]>([]);

  /*
   * Cards that are in the air towards a zone, by the zone they are headed for.
   *
   * The pile has ALREADY been told about the card - the view and the move
   * arrive in the same packet - so without this the card appeared on the
   * discard the instant somebody played it and then a second copy of it
   * floated over to join it. Same complaint as a drawn card showing in the
   * fan while it was still crossing the table, and the same answer: the
   * destination holds the card's place and does not draw it until it lands.
   */
  const [landing, setLanding] = useState<Record<string, Landing>>({});

  const move = view.lastMove;

  /*
   * Reached through a ref for the reason `useCounterEvent` documents:
   * `seatPoint` is rebuilt every render, so naming it here re-ran the effect
   * constantly, cancelled each removal timer, and left flights in the list
   * forever - piling up invisibly, because the animation ends at zero opacity.
   */
  const zones = view.view.zones;

  const latest = useRef({
    move,
    layout,
    variant,
    seat: view.seat,
    zones,
    seatPoint,
    zonePoint,
    slotPoint,
  });
  latest.current = {
    move,
    layout,
    variant,
    seat: view.seat,
    zones,
    seatPoint,
    zonePoint,
    slotPoint,
  };

  /*
   * The zones as they were BEFORE this packet.
   *
   * Kept by the effect at the bottom, which runs after the counter event
   * declared below it - so when a move fires, this still holds the table as it
   * was and the difference between the two is the card that just moved.
   */
  const previous = useRef(zones);

  useCounterEvent(move?.n ?? 0, () => {
    const at = latest.current;
    if (!at.move) return;

    const motion = (at.layout.motions ?? []).find((candidate) => candidate.move === at.move?.move);
    if (!motion) return;

    /*
     * Your own play is already on screen, once.
     *
     * On the hand screen `ThrownCards` draws the card you actually tapped,
     * face up, leaving your hand - so this face-down copy of the same move was
     * a SECOND card crossing the felt at the same moment. And because your own
     * chair is not drawn, it set off from where that chair would be: the
     * bottom middle of the ring, a few percent under the piles, which reads as
     * a card coming out of the draw pile rather than out of your hand.
     *
     * Plays only. A draw ends at SEAT_END, `throwFor` declines to animate one,
     * and this flight is the only thing that shows it - so the test is the
     * motion's destination, not the fact that the move was yours.
     *
     * This holds for a patience too, and it has to: the ring is `display:
     * none` on a board game - it exists to arrange seats, and there are none -
     * so a flight parented to it has no size, no position and nothing to
     * divide by. Every end of it collapsed to the middle of a box that was not
     * being drawn. The throw layer is a sibling of the ring rather than a
     * child, which is why the card turned from a stock is its job.
     *
     * The full variant keeps both: it is a table on a television with no hand
     * in front of it, so there is no throw to be the other half of.
     */
    if (at.variant === 'backdrop' && at.move.seat === at.seat && motion.to !== SEAT_END) return;

    /*
     * Where "the seat that acted" is, for this flight.
     *
     * Everybody else's seat is their card point on the ring. Your own is too,
     * except for a card coming TO you on your own screen: that one is about to
     * be held up in the draw slot, and stopping short of it left the card
     * hanging over the felt for a moment and then reappearing lower down. The
     * flight and the hold are one movement, so they share one endpoint.
     */
    const mine = at.variant === 'backdrop' && at.move.seat === at.seat;
    const seat = at.seatPoint(at.move.seat);
    const landing = (mine ? at.slotPoint() : null) ?? seat;

    const middle: Point = { x: 0.5, y: 0.5 };
    const end = (name: string, arriving: boolean): Point | null =>
      name === SEAT_END ? (arriving ? landing : seat) : (at.zonePoint(name) ?? middle);

    const from = end(motion.from, false);
    const to = end(motion.to, true);
    if (!from || !to) return;

    /*
     * Face up, unless the game says otherwise or the table cannot tell.
     *
     * A card played onto a pile is public the moment it lands, and flying a
     * blank rectangle to a place where the face is about to appear anyway told
     * the room nothing. The face comes from the destination rather than from
     * the move, because `lastMove` carries no card - see `landedCard`.
     *
     * Everything that should NOT show a face falls out of this rather than
     * needing to be listed: a draw ends at a seat and has no readable
     * destination, a hidden zone reports no cards, and a game that hides its
     * plays on purpose says `faceDown`.
     */
    const landed =
      motion.to === SEAT_END
        ? undefined
        : landedCard(previous.current?.[motion.to]?.cards, at.zones?.[motion.to]?.cards);

    /*
     * What the flight DRAWS, which is not the same question as what the
     * destination has to hold back.
     *
     * These used to be one value, and that made `faceDown` mean two things at
     * once: fly a back, and also let the pile show the card immediately. So a
     * card turned face down off the stock would have flown as a back towards a
     * waste that was already showing it - the reveal happening before the
     * animation instead of after it.
     *
     * Knowing WHICH card landed is what suppresses it; whether to show its
     * face in the air is a separate choice the game makes.
     */
    const face = motion.faceDown === true ? undefined : landed;

    const flight: Flight = { key: at.move.n, from, to, card: face };
    setFlights((current) => [...current, flight]);

    /*
     * Cleared when the card LANDS, not when the element is swept up - the
     * removal below is three hundred milliseconds later, deliberately, and a
     * pile that waited for it would sit empty for a third of a second after
     * the card had visibly arrived.
     */
    if (landed !== undefined) {
      const zone = motion.to;
      const had = previous.current?.[zone]?.cards;
      const entry: Landing = { card: landed, beneath: had?.[had.length - 1] };
      setLanding((current) => ({ ...current, [zone]: entry }));
      window.setTimeout(() => {
        setLanding((current) => {
          if (current[zone] !== entry) return current;
          const next = { ...current };
          delete next[zone];
          return next;
        });
      }, FLIGHT_MS);
    }

    /*
     * Removed on a timer rather than on animationend: a tab in the background
     * may never fire the event, and a card stuck mid-table is worse than a
     * missed animation. Comfortably longer than the flight itself.
     */
    window.setTimeout(() => {
      setFlights((current) => current.filter((candidate) => candidate.key !== flight.key));
    }, FLIGHT_MS + 300);
  });

  // After the counter event above, so that one sees the previous table. Same
  // ordering `useArrival` depends on, and for the same reason.
  useEffect(() => {
    previous.current = zones;
  }, [zones]);

  return { flights, landing };
}

/**
 * What each seat is currently saying, if anything.
 *
 * Driven by a per-seat counter for the reason `reshuffles` and `lastMove` are:
 * a flag is true for one tick and a client that rendered a moment later would
 * never see it. The counter also makes saying the SAME thing twice register as
 * two remarks rather than one that never changed.
 *
 * The first value seen is a baseline, so somebody opening a table mid-game does
 * not arrive to a row of bubbles repeating whatever was said before they came.
 */
function useSaid(view: MatchView): Record<number, string> {
  const { config } = useSession();
  const [showing, setShowing] = useState<Record<number, string>>({});

  const says = view.says;

  /*
   * A key built from the counters, not the object.
   *
   * `view.says` is a fresh object on every packet, so an effect depending on it
   * re-ran whenever anything else on the table changed - cancelling the
   * five-second timer and never setting another, which left a bubble up until
   * somebody spoke again. Same defect as the trick banner, same fix.
   */
  const spoken = Object.keys(says).reduce((total, seat) => total + (says[seat]?.n ?? 0), 0);

  const latest = useRef({ says, config });
  latest.current = { says, config };

  const counts = useRef<Record<number, number>>({});

  useCounterEvent(spoken, () => {
    const current = latest.current.says;

    const fresh: number[] = [];
    for (const key of Object.keys(current)) {
      const seat = Number(key);
      if ((current[key]?.n ?? 0) > (counts.current[seat] ?? 0)) fresh.push(seat);
      counts.current[seat] = current[key]?.n ?? 0;
    }
    if (fresh.length === 0) return;

    const added: Record<number, string> = {};
    for (const seat of fresh) {
      const entry = current[String(seat)];

      /*
       * Typed words are their own bubble; a phrase is looked up.
       *
       * The two are never both present — the protocol refuses a message
       * carrying both — so this is a choice rather than a precedence.
       */
      if (entry?.text !== undefined) {
        added[seat] = entry.text;
        continue;
      }

      const id = entry?.id ?? '';
      const phrase = (latest.current.config?.phrases ?? []).find((candidate) => candidate.id === id);
      // An id this client has no words for is dropped rather than shown raw:
      // "nice-cards" in a bubble is worse than no bubble.
      if (phrase) added[seat] = phrase.text;
    }
    if (Object.keys(added).length === 0) return;

    setShowing((was) => ({ ...was, ...added }));
    window.setTimeout(() => {
      setShowing((was) => {
        const next = { ...was };
        for (const seat of Object.keys(added)) delete next[Number(seat)];
        return next;
      });
    }, 5000);
  });

  return showing;
}

/** One seat button: what it says, what it does, and whether it is held. */
interface RingSeatAction {
  action: SeatActionWire;
  press: () => void;
  held: boolean;
}

function RingSeat({
  placement,
  seat,
  view,
  layout,
  zones,
  score,
  scoreLabel,
  tally,
  said,
  actions,
}: {
  placement: SeatPlacement;
  seat: MatchView['seats'][number];
  view: MatchView;
  layout: GameLayoutWire;
  zones: ZoneSpecWire[];
  score?: number | undefined;
  scoreLabel?: string | undefined;
  tally?: string | undefined;
  said?: string | undefined;
  actions: RingSeatAction[];
}) {
  const narrow = useNarrow();
  const isActive = view.activeSeats.indexOf(seat.seat) >= 0 && !view.paused;
  const classes = ['ring-seat'];
  if (isActive) classes.push('active');
  /*
   * Raised while speaking, because the bubble cannot raise itself.
   *
   * `.ring-seat` is transformed, and a transform creates a stacking context -
   * so a z-index on the bubble only orders it WITHIN its own seat, and the
   * next seat in the DOM painted straight over it. Lifting the seat is what
   * actually lifts the bubble, so the class goes here rather than on the
   * bubble.
   */
  if (said) classes.push('speaking');
  if (!seat.connected) classes.push('offline');
  if (seat.away) classes.push('away');
  if (placement.self) classes.push('self');
  /*
   * Which way the speech bubble opens.
   *
   * It always opened to the right, which is fine for the left half of a ring
   * and runs off the screen on the right half - at a four-player table the
   * seat on the right had its words cut in half, and at six two seats did. A
   * bubble you have to open the chat history to read is not a bubble.
   *
   * Decided from the seat's own position rather than from its index, because
   * the ring rotates so that YOUR seat is at the bottom - which seat is on the
   * right depends on who is looking.
   */
  if (placement.x > 0.5) classes.push('right-half');

  return (
    <div
      className={classes.join(' ')}
      style={{ left: percent(placement.x), top: percent(placement.y) }}
    >
      {/* The clock is drawn around this seat's avatar when it is their turn
          (H11), so whose turn it is and how long they have are one thing to
          look at rather than two. */}
      <SeatAvatarRing turn={isActive && view.turn?.seat === seat.seat ? view.turn : null}>
        {avatarEmoji(seat.avatarId)}
      </SeatAvatarRing>

      <span className="ring-seat-meta">
        <span className="seat-name">
          {seat.displayName}
          {seat.isHost ? (
            <span className="host-tag">
              {/* A crown and the word, not the crown alone: a glyph with no
                  text is a thing to decode, and "who is running this" is
                  exactly what a new arrival is trying to find out. */}
              <span aria-hidden="true">♛</span> host
            </span>
          ) : null}
        </span>

        {score !== undefined ? (
          <span className="seat-score">
            <span className="seat-score-value">{score}</span>
            {scoreLabel ? <span className="seat-score-label">{scoreLabel}</span> : null}
          </span>
        ) : null}

        {/* Tricks taken against tricks bid, which is one fact rather than two
            - see `seatTally` in the layout. */}
        {tally ? <span className="seat-tally">{tally}</span> : null}

        {!seat.connected ? <span className="seat-state">offline</span> : null}
        {seat.away && seat.connected ? <span className="seat-state">away</span> : null}
      </span>

      {/*
       * What this seat last said.
       *
       * Drawn on the seat rather than in a log: a remark belongs to a person,
       * and at a real table you know who spoke because you can see them. It
       * expires on its own - see `useSaid`.
       */}
      {said ? (
        /* The same colour this seat has in the chat log (H19), so a remark on
           the table and the same remark in the log read as one person. */
        <span className="seat-bubble" style={{ '--who': seatColour(seat.seat) } as React.CSSProperties}>
          {said}
        </span>
      ) : null}

      <span className="badges">
        {(layout.seatBadges ?? []).map((zoneName) => {
          const count = badgeCount(zoneName, seat.seat, zones, view.view.zones);
          if (count === null) return null;
          return (
            <span className="badge" key={zoneName}>
              <span className="badge-label">{zoneName}</span>
              <span className="badge-count">{count}</span>
            </span>
          );
        })}
      </span>

      {/*
       * A button about this person: "last card!", pointed at them.
       *
       * On the seat rather than in a row somewhere, because it is an accusation
       * and an accusation has a target - a row of four buttons labelled with
       * names is a form to fill in. It is here the whole time, on everybody,
       * which is what makes the table not stop for it.
       *
       * Held, not hidden, while the seat's own head start runs: a control that
       * disappears for a second and a half is one you tap through into whatever
       * moved underneath it. See `grace` in the layout.
       */}
      {actions.length > 0 ? (
        <span className="seat-actions">
          {actions.map(({ action, press, held }) => (
            <button
              key={action.move}
              type="button"
              className={`seat-action${held ? ' held' : ''}`}
              onClick={press}
              disabled={held}
              title={held ? (action.graceNote ?? action.label) : action.label}
              aria-label={`${action.label}: ${seat.displayName}`}
            >
              <span aria-hidden="true">{action.mark}</span>
            </button>
          ))}
        </span>
      ) : null}

      {/*
       * Per-seat zones the layout places in the seat region: the cards
       * somebody is holding, the tricks they have taken, anything face down in
       * front of them.
       *
       * Drawn at BOTH sizes now. It was shared-screen-only on the grounds that
       * a phone has somebody's hand over it - but the phone's backdrop IS the
       * table for anybody playing without a television, and how many cards the
       * player to your left is holding is not reference material, it is the
       * game.
       */}
      <span className="seat-zones">
        {layout.table
          .filter((placement2) => placement2.region === 'seat')
          .map((placement2) => {
            /*
             * Cards become a count when the ring has no room for them (D82).
             *
             * A seat is centred on its point, so its width spreads both ways —
             * and a seat carrying a row of cards ran off the left edge at every
             * phone width, silently, because nothing scrolled.
             */
            const drawn = seatPlacement(placement2, narrow);
            return resolve(
              placement2,
              zones,
              view.view.zones,
              view.seats.length,
              seat.seat,
            ).map((resolved) => (
              <Zone
                key={resolved.id}
                placement={drawn}
                zone={resolved.view}
                small
                maxBacks={backsShown(Number.MAX_SAFE_INTEGER, narrow)}
              />
            ));
          })}
      </span>
    </div>
  );
}

/**
 * Where a pyramid zone's cards sit, if this game has one.
 *
 * A platform convention like `trickPlays`: a fixed name with a fixed shape,
 * read straight out of the view. Absent for every game that is not a patience,
 * and the renderer simply draws nothing positional.
 */
function pyramidSlots(view: MatchView): number[] | undefined {
  const raw = view.view['slots'];
  if (!Array.isArray(raw)) return undefined;
  return raw.filter((value): value is number => typeof value === 'number');
}

export function TableView({
  view,
  layout,
  zones,
  variant,
  onZone,
  onSeatAction,
}: {
  view: MatchView;
  layout: GameLayoutWire;
  zones: ZoneSpecWire[];
  variant: TableVariant;
  /*
   * What pressing a pile on the table does, asked per zone.
   *
   * This screen was a picture for a long time, and the note at the top of the
   * file still says what it is FOR - showing the room who acted. But a picture
   * is the wrong thing when the pile you draw from is drawn on it. The
   * renderer still decides nothing: it asks, and gets back a handler or
   * nothing.
   *
   * Absent on a television, which nobody is going to reach out and touch.
   */
  onZone?: ((zone: string) => (() => void) | undefined) | undefined;
  /*
   * What pressing a seat does, asked per seat and per move - the same asking
   * shape as `onZone`, and for the same reason. A move that names a person is
   * declared in `seatActions`; whether this screen can send one is not the
   * renderer's business, so it asks and gets back a handler or nothing.
   *
   * Absent on a television, like `onZone`.
   */
  onSeatAction?: ((move: string, seat: number) => (() => void) | undefined) | undefined;
}) {
  /*
   * A running score, only if the game named one.
   *
   * Looking for a field called "scores" would be a guess that happens to work
   * for Hearts; declaring it in the layout is what keeps this renderer honest
   * about knowing no game.
   */
  /*
   * The sides, when the game is played by them.
   *
   * A partnership game was showing four independent seat scores on the table
   * while the standings - the screen nobody is looking at mid-hand - were the
   * only place the numbers that actually decide the game appeared. In Spades a
   * seat's own score is not a thing that exists.
   */
  const sides = sidesOf(view, layout);

  const scoreField = layout.seatScore?.field;
  const scores = scoreField ? view.view[scoreField] : undefined;
  const seatScores = Array.isArray(scores) ? (scores as unknown[]) : null;
  const scoreOf = (seat: number): number | undefined => {
    const value = seatScores?.[seat];
    return typeof value === 'number' ? value : undefined;
  };

  /*
   * "1/3" per seat, when the game declares a pair worth reading together.
   *
   * Built here rather than in the seat so the field lookups happen once per
   * render instead of once per chair.
   */
  const tallySpec = layout.seatTally;
  const tallyOf = (seat: number): string | undefined => {
    if (!tallySpec) return undefined;
    const values = view.view[tallySpec.value];
    const targets = view.view[tallySpec.target];
    if (!Array.isArray(targets)) return undefined;
    // The formatting rule, including what a negative target means, lives in
    // `render/tally.ts` - the standings panel shows the same pair and had its
    // own copy of it.
    return tallyText(
      Array.isArray(values) ? (values as unknown[])[seat] : undefined,
      (targets as unknown[])[seat],
    ) ?? undefined;
  };

  /*
   * The suit a wild named, if this game has such a thing and one is in force.
   *
   * Read through the layout rather than by guessing a field name, so a game
   * that has no wild simply declares nothing and nothing is drawn.
   */
  const declaredSpec = layout.declaredSuit;
  const declaredValue = declaredSpec ? view.view[declaredSpec.field] : null;
  const declaredFor = (zone: string): string | undefined =>
    declaredSpec && declaredSpec.zone === zone && typeof declaredValue === 'string'
      ? declaredValue
      : undefined;

  const plays = trickPlays(view);
  const playedBySeat: Record<number, string> = {};
  for (const play of plays) playedBySeat[play.seat] = play.card;

  /*
   * A trick placed per seat is not also drawn in the middle.
   *
   * Otherwise every card appears twice, which reads as a bug rather than as a
   * design choice.
   */
  const centre = layout.table.filter(
    (placement) =>
      placement.region !== 'seat' && !(plays.length > 0 && placement.zone === 'trick'),
  );

  const ringRef = useRef<HTMLDivElement | null>(null);
  const seatsByNumber = new Map(view.seats.map((seat) => [seat.seat, seat]));
  const placements = placeSeats(view.seatCount || view.seats.length, view.seat);

  /*
   * Buttons that name a person - see `seatActions` in the toolkit's layout.
   *
   * The counts each one watches are gathered here because this is where the
   * seats are: the head start is a fact about a seat's hand, and the hand
   * counts are public (they are what the badges draw).
   */
  const actions = layout.seatActions ?? [];
  const watched: CountsByMove = {};
  for (const action of actions) {
    if (!action.grace) continue;
    const bySeat: { [seat: number]: number } = {};
    for (const placement of placements) {
      const count = badgeCount(action.grace.zone, placement.seat, zones, view.view.zones);
      if (count !== null) bySeat[placement.seat] = count;
    }
    watched[action.move] = bySeat;
  }
  const held = useGrace(actions, watched);

  /**
   * The seat buttons for one seat: what it says, whether it is held, and what
   * pressing it does. Empty unless the server offered the move on that seat.
   */
  const actionsFor = (seat: number): RingSeatAction[] => {
    if (!onSeatAction) return [];
    const out: RingSeatAction[] = [];
    for (const action of actions) {
      if (!seatsOffered(view.legalMoves, action.move).has(seat)) continue;
      const press = onSeatAction(action.move, seat);
      if (!press) continue;
      out.push({ action, press, held: held(action.move, seat) });
    }
    return out;
  };

  /*
   * A motion to or from a seat aims at the seat's CARD point, not its chair.
   *
   * That is where a card it played already appears, so the flight lands where
   * the eye is going anyway. Your own seat has no chair drawn, but it does
   * have a card point - directly above your hand - so your own draws animate
   * like everybody else's. Your own PLAYS do not: see the note in
   * `useFlights` about the card you tapped already being on its way.
   */
  const seatPoint = useCallback(
    (seat: number) => {
      const placement = placements.find((candidate) => candidate.seat === seat);
      return placement ? { x: placement.cardX, y: placement.cardY } : null;
    },
    [placements],
  );

  /*
   * Where a named zone actually is, as a fraction of the ring.
   *
   * Read from the DOM because the middle zones are laid out by flexbox: the
   * draw pile's position is a result of the layout, not something computed
   * here. Measured at the moment a card flies rather than cached, so a resized
   * window or a zone that has only just appeared is never animated to where it
   * used to be.
   */
  /*
   * An element's middle, as a fraction of the ring.
   *
   * A fraction, like `placeSeats` gives - NOT a percentage. `percent()` does
   * the multiplying, and doing it here as well put the draw pile's end of
   * every flight forty-seven ring widths off the table.
   */
  const pointOf = useCallback((element: Element | null): Point | null => {
    const ring = ringRef.current;
    if (!ring || !element) return null;

    const bounds = ring.getBoundingClientRect();
    if (bounds.width === 0 || bounds.height === 0) return null;
    const box = element.getBoundingClientRect();

    return {
      x: (box.left + box.width / 2 - bounds.left) / bounds.width,
      y: (box.top + box.height / 2 - bounds.top) / bounds.height,
    };
  }, []);

  /*
   * The ring first, then anywhere.
   *
   * Every zone a flight aimed at used to be on the felt, so scoping the search
   * to the ring said "this is the table's own pile" and cost nothing. A
   * patience puts its stock on the board below instead, and a card turned from
   * it would have flown to the middle of an empty table - so the fallback is a
   * document-wide search, the same one the draw slot needs and for the same
   * reason: `pointOf` works in viewport pixels and only divides by the ring at
   * the end.
   */
  const zonePoint = useCallback(
    (zone: string): Point | null =>
      pointOf(
        ringRef.current?.querySelector(`[data-zone="${zone}"]`) ??
          document.querySelector(`[data-zone="${zone}"]`),
      ),
    [pointOf],
  );

  /*
   * Where a seat's chair is, as opposed to the felt in front of it.
   *
   * `seatPoint` is the card point - close in, where a played card or a bet
   * sits. Chips come out of the player rather than off the felt, so a bet
   * starts here and travels the short distance between the two.
   */
  const chairPoint = useCallback(
    (seat: number) => {
      const placement = placements.find((candidate) => candidate.seat === seat);
      return placement ? { x: placement.x, y: placement.y } : null;
    },
    [placements],
  );

  /*
   * Where the pot is.
   *
   * Measured rather than computed, for the reason `zonePoint` gives: the
   * middle band is laid out by flexbox, so the pot's position is a result of
   * that layout. Null when this game has no pot, which is every game but one.
   */
  const potPoint = useCallback(
    (): Point | null => pointOf(ringRef.current?.querySelector('.chip-stack.pot') ?? null),
    [pointOf],
  );

  /*
   * Where a card drawn by the person holding this screen comes to rest.
   *
   * Outside the ring, unlike every other end of a flight - the slot belongs to
   * the hand, which is a sibling of the table rather than part of it. Measured
   * the same way regardless, because `pointOf` works in viewport pixels and
   * only divides by the ring at the end.
   *
   * Null on a table with no hand in front of it, which is a television, where
   * there is no slot and a seat point is the right answer anyway.
   */
  const slotPoint = useCallback(
    (): Point | null => pointOf(document.querySelector('.draw-slot')),
    [pointOf],
  );

  const { flights, landing } = useFlights(view, layout, variant, seatPoint, zonePoint, slotPoint);
  const payoutFlights = usePayout(view, seatPoint, potPoint);
  const chipFlights = useChipMoves(view, layout, chairPoint, seatPoint, potPoint);

  /*
   * Every card currently in the air, whichever zone it is bound for.
   *
   * A trick played into a positional layout is not drawn in a zone at all -
   * it is a card beside its player's chair - so it cannot be found by looking
   * up a zone name, only by asking whether this particular card is still
   * travelling.
   */
  const inAir = new Set(Object.values(landing).map((entry) => entry.card));
  const said = useSaid(view);

  return (
    <div
      /*
       * `has-chips` reserves a lane under the piles for the viewer's own bet.
       * See the stylesheet: on a table with money there is one more thing to
       * fit between the middle and the hand, and it has to come from
       * somewhere.
       */
      className={`ring ${variant}${layout.chips ? ' has-chips' : ''}`}
      ref={ringRef}
      /* How many cards can land around the ring at once, which is what decides
         how big each one may be. See `.ring-play` in the stylesheet. */
      style={{ '--seats': placements.length } as React.CSSProperties}
    >
      {/*
        The house mark, printed on the felt (C10).

        Above the ring rather than in the middle of it, in a band the ring's
        top margin reserves - see `.table-mark`. It sat at the centre until M2
        measured what was actually happening there: every one of the nine
        multiplayer games draws its central zones at the same spot, so the mark
        was underneath the piles in all of them rather than beside them.

        Still the first child, and still for the original reason - a logo on a
        table is beneath the game, not over it. The band means nothing should
        reach it now; source order means nothing can.

        Rendered on both surfaces and drawn on one. `--mark-w` is zero on a
        phone, which collapses this to nothing and reserves nothing for it; the
        stylesheet carries why. A branch here instead would put the decision in
        two places and let them disagree.

        Painted from `--table-mark`, which only Classic sets, so a themed table
        simply has no mark rather than wearing ours.

        `aria-hidden`: it is the same wordmark the app already announces
        everywhere else, and a screen reader meeting "Revelry" in the middle of a
        card table learns nothing it did not know.
      */}
      <div className="table-mark" aria-hidden="true" />

      {placements.map((placement) => {
        const seat = seatsByNumber.get(placement.seat);
        const played = playedBySeat[placement.seat];

        return (
          <div key={placement.seat}>
            {/*
             * Your own chair is not drawn, only the card you played from it.
             *
             * It would sit exactly where your hand does, and you are the one
             * person in the room who does not need telling who you are — your
             * name and clock are in the bar directly above your cards.
             */}
            {placement.self ? null : seat ? (
              <RingSeat
                placement={placement}
                seat={seat}
                view={view}
                layout={layout}
                zones={zones}
                score={scoreOf(placement.seat)}
                scoreLabel={layout.seatScore?.label}
                tally={tallyOf(placement.seat)}
                said={said[placement.seat]}
                actions={actionsFor(placement.seat)}
              />
            ) : (
              // An empty chair is drawn rather than left out: "waiting for two
              // more" means less at a glance than two gaps in the ring.
              <div
                className="ring-seat empty"
                style={{ left: percent(placement.x), top: percent(placement.y) }}
              >
                <span className="avatar" aria-hidden="true">
                  ·
                </span>
                <span className="seat-name">Empty</span>
              </div>
            )}

            {/*
             * Full size, deliberately.
             *
             * This was drawn with the small card at first and the trick was
             * illegible on a phone — a 26px card carries an 8px rank. The small
             * card is for a *pile* of tricks already taken, never for a card
             * somebody has just played and everyone is reading.
             */}
            {played ? (
              <div
                className="ring-play"
                style={{ left: percent(placement.cardX), top: percent(placement.cardY) }}
              >
                <Card card={played} arriving={inAir.has(played)} />
              </div>
            ) : null}

            {/*
              What this seat has put in this round, on the felt in front of
              them - at the card point, which is where a player pushes chips.

              The viewer's own bet is NOT drawn here; it is anchored under the
              piles instead. Your chair is the bottom of the ring and so is
              the middle band, so any fraction of the ring that looks clear on
              one screen lands on the deck when the hand panel opens and the
              ring shrinks. Measured, twice.
            */}
            {placement.self ? null : (
              <div
                className="ring-bet"
                style={{ left: percent(placement.cardX), top: percent(placement.cardY) }}
              >
                <ChipStack amount={committedOf(view, layout, placement.seat)} />
              </div>
            )}
          </div>
        );
      })}

      <div className="ring-middle">
        {/*
          The pot, directly above the piles and out of their flow.

          Three placements were tried. In the band's flow it sat beside the
          deck at badge size. As a row of the band it made the band taller,
          and the band is centred on a point - so it grew upward and the pot
          ended up over the far player's name and chip count. Against the ring
          at a fixed percentage it landed on the board, because the ring's
          own height changes when the hand panel opens.

          Anchored to the band it cannot overlap the band or anything in it,
          at any height, and it moves when the piles move.
        */}
        <div className="ring-pot">
          <ChipStack
            amount={middleOf(view, layout, view.seats.length)}
            label="Pot"
            className="pot"
          />
        </div>

        {/*
          Your own bet, on the felt below the piles.

          Between you and the middle, which is where a player pushes chips and
          where everybody else's is drawn. It took a reserved lane to fit it -
          see `.ring.has-chips` - because your chair and the piles are both at
          the bottom of the ring, and every spot tried down here landed on the
          deck or on the hand panel's grip.

          Anchored to the band rather than to a fraction of the ring: the ring
          loses eighty pixels of height when the hand panel opens, and every
          percentage that looked clear with it shut was wrong with it open.

          No label. It is in front of you, like everybody else's is in front
          of them, and a caption on only one seat's money reads as a
          difference in kind.
        */}
        {view.seat === null ? null : (
          <div className="ring-bet own">
            <ChipStack amount={committedOf(view, layout, view.seat)} />
          </div>
        )}

        {centre.map((placement) =>
          resolve(placement, zones, view.view.zones, view.seats.length).map((resolved) => (
            // Zone tags itself with `data-zone`, which is how a card in flight
            // finds the real pile rather than aiming at the middle.
            <Zone
              key={resolved.id}
              placement={placement}
              zone={resolved.view}
              declared={declaredFor(placement.zone)}
              slots={pyramidSlots(view)}
              arriving={landing[placement.zone]?.card}
              beneath={landing[placement.zone]?.beneath}
              onZone={onZone?.(placement.zone)}
            />
          )),
        )}
        <Shuffling count={reshuffleCount(view)} />

        {/*
          The sides' running scores, on the shared screen.

          Named by their members rather than "Us" and "Them": this is the one
          screen where nobody's point of view is the right one, and it is also
          how people say it out loud.
        */}
        {sides ? (
          <div className="table-sides" aria-label={layout.teams?.label ?? 'Teams'}>
            {sides.map((side, index) => (
              <div className="table-side" key={`side-${String(index)}`}>
                <span className="table-side-name">
                  {sideName(side, (seat) =>
                    view.seats.find((info) => info.seat === seat)?.displayName ??
                    `Seat ${String(seat + 1)}`,
                  )}
                </span>
                <span className="table-side-score">{side.score ?? '—'}</span>
              </div>
            ))}
          </div>
        ) : null}
        {variant === 'full' && view.lifecycle === 'between' ? (
          <div className="table-result">
            <Scoreboard view={view} layout={layout} />
          </div>
        ) : null}
      </div>

      {/*
       * Paused, said loudly.
       *
       * A pause used to show only as the absence of a highlight on the active
       * seat, plus the word "paused" in small text on the phone. A table that
       * has simply stopped is indistinguishable from one waiting on a player
       * who is thinking, and the room spends a minute wondering whose turn it
       * is. This covers the table because that is the point - nothing on it is
       * actionable while the game is held.
       *
       * `role="status"` rather than `alert`: it is a state the room is in, not
       * an interruption, and a screen reader should announce it without
       * cutting off whatever it was saying.
       */}
      {/*
       * Cards in flight.
       *
       * Face down in both directions and on purpose: a drawn card is private,
       * and a played one is already face up on the discard by the time this
       * lands - drawing it here too would show the same card twice, a beat
       * apart, which reads as a duplicate rather than as a movement.
       *
       * aria-hidden because it is the same event the zones and badges already
       * report; a screen reader should hear it once, not three times.
       */}
      {[...flights, ...payoutFlights, ...chipFlights].map((flight) => (
        <div
          key={flight.key}
          className={
            flight.chips === undefined
              ? `card-flight${flight.card === undefined ? '' : ' face'}`
              : 'card-flight chips'
          }
          aria-hidden="true"
          data-chips={flight.kind}
          style={
            {
              '--from-x': percent(flight.from.x),
              '--from-y': percent(flight.from.y),
              '--to-x': percent(flight.to.x),
              '--to-y': percent(flight.to.y),
              // The one place the flight's length is written. The reveal has
              // to wait for the card to land, so the number cannot live only
              // in the stylesheet.
              '--flight-ms': `${String(FLIGHT_MS)}ms`,
            } as React.CSSProperties
          }
        >
          {flight.chips === undefined ? (
            flight.card === undefined ? null : <Card card={flight.card} />
          ) : (
            <ChipStack amount={flight.chips} />
          )}
        </div>
      ))}

      {/* The trick that just finished, held up before the table forgets it. */}
      <TrickWon view={view} />
      <Showdown view={view} />

      {view.paused ? (
        <div className="table-paused" role="status">
          <span className="table-paused-mark" aria-hidden="true">
            ❚❚
          </span>
          <span className="table-paused-title">Paused</span>
          <span className="table-paused-note">
            {view.isHost
              ? 'Resume from the table menu, top right'
              : 'The host has paused the game'}
          </span>
        </div>
      ) : null}
    </div>
  );
}
